import { cloneElement, isValidElement } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { Box, Fab, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { atLeastOneRequired } from '~/src/Lib/PropTypes'
import getClassPrefixer from '~/src/UI/classPrefixer'

import { calcFluidTypography } from '../Theme/typography'

const displayName = 'EmptyState'
const classes = getClassPrefixer(displayName)

const Root = styled(Box, { name: displayName })(({ theme }) => ({
  gridColumn: '1 / 4',
  gridRow: '2 / 5',
  display: 'flex',
  height: '100%',
  width: '100%',
  flexWrap: 'wrap',
  alignItems: 'center',
  placeContent: 'center',
  justifyContent: 'center',
  gap: '1rem',
  [`&.${classes.inline} .${classes.headerIcon}`]: {
    fontSize: theme.spacing(9),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  [`& .${classes.headerIcon}`]: {
    fontSize: calcFluidTypography({ maxFont: 96, minFont: 72, baseRem: 2 }),
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  [`& .${classes.headerIconContainer}`]: {
    display: 'flex',
    placeItems: 'center',
    overflow: 'hidden',
  },
  [`&.${classes.inline} .${classes.headerIconContainer}`]: {
    height: '100%',
  },
  [`& .${classes.heading}`]: {
    flex: '0 0 100%',
    textAlign: 'center',
  },
  [`&.${classes.inline} .${classes.heading}`]: {
    flex: '0 0 auto',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      ...theme.typography.body1,
      flex: '0 1 auto',
      paddingBottom: theme.spacing(),
    },
  },
  [`&:not(.${classes.inline}) .${classes.fabContainer}`]: {
    display: 'inline-flex',
    flex: '0 0 100%',
    justifyContent: 'center',
  },
  [`& .${classes.fab}`]: {
    padding: '0 1rem',
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingLeft: 2,
    },
  },
  [`& .${classes.fabIcon}`]: {
    marginRight: 4,
    fill: theme.palette.text.primary,
  },
}))

/**
 * A component to display when data is not present with an optional call to action
 *
 * @example
 *   import BubbleChart from '@mui/icons-material/BubbleChart'
 *   import Cake from '@mui/icons-material/Cake'
 *   import Fastfood from '@mui/icons-material/Fastfood'
 *   import Timelapse from '@mui/icons-material/Timelapse'
 *   ;<Box display="flex" style={{ gap: '2rem' }}>
 *     <EmptyState
 *       HeaderIcon={Cake}
 *       heading="It's (not) your birthday!"
 *       fabLabel="Make it my birthday*"
 *       fabProps={{ style: { marginBottom: '2rem' } }}
 *     >
 *       <T.Tiny>* We can't really make it your birthday, but, if we could we would. Really.</T.Tiny>
 *     </EmptyState>
 *     <EmptyState
 *       HeaderIcon={<BubbleChart htmlColor="orange" />}
 *       heading={
 *         <T.H5 color="error">We know your data is around here somewhere, but we can't find it right now.</T.H5>
 *       }
 *       fabLabel="Bring back my data!"
 *       fabProps={{ color: 'secondary' }}
 *     />
 *     <EmptyState
 *       HeaderIcon={<Timelapse color="primary" />}
 *       heading="You've been working for hours. It's time for a break."
 *       fabLabel="You're right. I'm hungry."
 *       FabIcon={Fastfood}
 *     />
 *   </Box>
 *
 * @component
 * @alias EmptyState
 * @named_export EmptyStateComponent
 * @composes
 * from: '@mui/material/Typography'
 * propName: headingProps
 * link: https://material-ui.com/api/typography/#props
 * @composes
 * from: '@mui/material/Fab'
 * propName: fabProps
 * link: https://material-ui.com/api/fab/#props
 */

export const EmptyStateComponent = ({
  children,
  className = undefined,
  color = 'primary',
  dataTestId = 'aroya-empty-state',
  FabIcon = undefined,
  fabLabel = undefined,
  fabProps = undefined,
  HeaderIcon = undefined,
  heading = undefined,
  headingProps = undefined,
  inline = undefined,
  style = undefined,
  ...restProps
}) => (
  <Root
    data-testid={dataTestId}
    className={classNames(className, {
      [classes.inline]: inline,
    })}
    style={style}
    sx={restProps}
  >
    {HeaderIcon ? (
      <div className={classes.headerIconContainer}>
        {isValidElement(HeaderIcon) ? (
          cloneElement(HeaderIcon, {
            className: HeaderIcon.props.className
              ? classNames(classes.headerIcon, HeaderIcon.props.className)
              : classes.headerIcon,
          })
        ) : (
          <HeaderIcon className={classes.headerIcon} />
        )}
      </div>
    ) : null}
    {isValidElement(heading) ? (
      cloneElement(heading, {
        className: heading.props.className ? classNames(classes.heading, heading.props.className) : classes.heading,
      })
    ) : (
      <Typography gutterBottom={!inline} variant="subtitle1" className={classes.heading} {...headingProps}>
        {heading}
      </Typography>
    )}
    {fabLabel || FabIcon ? (
      <div className={classes.fabContainer}>
        <Fab variant="extended" color={color} size="small" {...fabProps} className={classes.fab}>
          {FabIcon ? <FabIcon className={classes.fabIcon} /> : null}
          {fabLabel}
        </Fab>
      </div>
    ) : null}
    {children}
  </Root>
)
EmptyStateComponent.displayName = 'EmptyState'
EmptyStateComponent.propTypes = {
  ...atLeastOneRequired({
    children: PropTypes.node,
    heading: PropTypes.node,
  }),
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  FabIcon: PropTypes.elementType,
  fabLabel: PropTypes.node,
  fabProps: PropTypes.shape({
    startIcon: PropTypes.element,
    endIcon: PropTypes.element,
    onClick: PropTypes.func,
  }),
  HeaderIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  headingProps: PropTypes.shape({
    color: PropTypes.string,
    gutterBottom: PropTypes.bool,
    variant: PropTypes.string,
  }),
  inline: PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
}

export default EmptyStateComponent
